import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const MAX_POLLS = 8;

export default function BillingSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [state, setState] = useState("checking"); // checking | done | pending | error
    const polls = useRef(0);

    useEffect(() => {
        const sessionId = params.get("session_id");
        const isSetup = params.get("setup") === "1";
        if (!sessionId) { setState("error"); return; }
        let alive = true;

        // Card-setup flow: confirm the card was stored on the trialing subscription.
        if (isSetup) {
            (async () => {
                try {
                    await api.get(`/billing/setup/confirm?session_id=${sessionId}`);
                    if (!alive) return;
                    setState("card");
                    refresh?.();
                } catch {
                    if (alive) setState("error");
                }
            })();
            return () => { alive = false; };
        }

        const poll = async () => {
            try {
                const { data } = await api.get(`/billing/status/${sessionId}`);
                if (!alive) return;
                if (data.applied) {
                    setState("done");
                    refresh?.();
                    return;
                }
                polls.current += 1;
                if (polls.current >= MAX_POLLS) { setState("pending"); return; }
                setTimeout(poll, 2000);
            } catch {
                if (!alive) return;
                polls.current += 1;
                if (polls.current >= MAX_POLLS) { setState("error"); return; }
                setTimeout(poll, 2000);
            }
        };
        poll();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-[70vh] grid place-items-center px-4" data-testid="billing-success">
            <div className="max-w-md w-full text-center bg-surface border border-border rounded-sm p-8">
                {state === "checking" && (
                    <>
                        <Loader2 className="w-12 h-12 text-volt animate-spin mx-auto mb-4" />
                        <h1 className="font-heading text-2xl font-bold mb-1">Confirming your subscription…</h1>
                        <p className="text-sm text-muted-foreground">One moment while we activate your access.</p>
                    </>
                )}
                {state === "done" && (
                    <>
                        <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-4" />
                        <h1 className="font-heading text-2xl font-bold mb-1" data-testid="billing-success-title">You're all set!</h1>
                        <p className="text-sm text-muted-foreground mb-6">Your 7-day free trial is active. Full tactical coaching is unlocked.</p>
                        <button onClick={() => navigate("/dashboard")} data-testid="go-dashboard"
                            className="h-11 px-6 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] inline-flex items-center gap-2">
                            Go to Dashboard <ArrowRight className="w-4 h-4" />
                        </button>
                    </>
                )}
                {state === "card" && (
                    <>
                        <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-4" />
                        <h1 className="font-heading text-2xl font-bold mb-1" data-testid="billing-card-saved">Card saved — you're covered!</h1>
                        <p className="text-sm text-muted-foreground mb-6">Your Pro trial continues free. After your trial ends, your card is charged automatically — cancel anytime before then.</p>
                        <button onClick={() => navigate("/dashboard")} data-testid="go-dashboard-card"
                            className="h-11 px-6 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] inline-flex items-center gap-2">
                            Go to Dashboard <ArrowRight className="w-4 h-4" />
                        </button>
                    </>
                )}
                {state === "pending" && (
                    <>
                        <Loader2 className="w-12 h-12 text-warn mx-auto mb-4" />
                        <h1 className="font-heading text-2xl font-bold mb-1">Almost there…</h1>
                        <p className="text-sm text-muted-foreground mb-6">Your payment is processing. Your access will unlock shortly — refresh in a minute.</p>
                        <Link to="/dashboard" className="text-volt hover:underline text-sm">Go to Dashboard</Link>
                    </>
                )}
                {state === "error" && (
                    <>
                        <XCircle className="w-12 h-12 text-alert mx-auto mb-4" />
                        <h1 className="font-heading text-2xl font-bold mb-1">Something went wrong</h1>
                        <p className="text-sm text-muted-foreground mb-6">We couldn't confirm this checkout. If you were charged, your access will activate automatically.</p>
                        <Link to="/pricing" className="text-volt hover:underline text-sm">Back to Pricing</Link>
                    </>
                )}
            </div>
        </div>
    );
}
